import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Button,
  Table,
  StatusIndicator,
  Spinner,
  BreadcrumbGroup,
  Alert,
  Modal,
  Select,
  FormField,
  Input,
  Textarea,
  ColumnLayout,
  Icon,
  TextFilter,
} from '@cloudscape-design/components';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { useNotifications } from './NotificationContext';
import AppLayoutWrapper from './AppLayoutWrapper';

export default function WorkflowList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { error: notifyError, success: notifySuccess } = useNotifications();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', status: 'active' });
  const [retryingId, setRetryingId] = useState(null);
  const [filteringText, setFilteringText] = useState('');
  const [sortingColumn, setSortingColumn] = useState(null);
  const [sortingDescending, setSortingDescending] = useState(true);

  const filteredWorkflows = useMemo(() => {
    if (!filteringText) return workflows;
    const lower = filteringText.toLowerCase();
    return workflows.filter((wf) =>
      (wf.name && wf.name.toLowerCase().includes(lower)) ||
      (wf.type && wf.type.toLowerCase().includes(lower))
    );
  }, [workflows, filteringText]);

  const sortedWorkflows = useMemo(() => {
    if (!sortingColumn) return filteredWorkflows;
    const sorted = [...filteredWorkflows].sort((a, b) => {
      const f = sortingColumn.sortingField;
      const getVal = (item) => {
        if (f === 'name') return item.name || '';
        if (f === 'type') return item.type || '';
        if (f === 'status') return item.sync_status || item.status || '';
        return '';
      };
      const aVal = getVal(a);
      const bVal = getVal(b);
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return sortingDescending ? [...sorted].reverse() : sorted;
  }, [filteredWorkflows, sortingColumn, sortingDescending]);

  const accessToken = user?.access_token || user?.token;

  const fetchWorkflows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!accessToken) {
        setLoading(false);
        return;
      }
      const response = await axios.get('/api/workflows/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const raw = response.data?.data || response.data || [];
      setWorkflows(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error('Workflow fetch error:', err);
      setError('Unable to load workflows.');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (user) fetchWorkflows();
  }, [user, fetchWorkflows]);

  const effectiveWfId = (wf) => wf.id || wf.adexcloud_workflow_id;

  const handleRetrySync = async (wf) => {
    const wfId = wf.id;
    if (!wfId) return;
    setRetryingId(wfId);
    try {
      const resp = await axios.post(`/api/workflows/${wfId}/retry-sync/`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (resp.data?.status === 'success') {
        notifySuccess('Workflow synced to adexcloud successfully');
        fetchWorkflows();
      } else {
        notifyError(resp.data?.message || 'Sync retry failed');
      }
    } catch (err) {
      notifyError('Retry sync failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setRetryingId(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedWorkflow) return;
    const wfId = effectiveWfId(selectedWorkflow);
    if (!wfId) {
      notifyError('Cannot delete: workflow has no valid ID');
      return;
    }
    try {
      await axios.delete(`/api/workflows/${wfId}/delete/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setWorkflows((prev) => prev.filter((w) => effectiveWfId(w) !== wfId));
      setDeleteModalVisible(false);
      notifySuccess('Workflow deleted');
    } catch (err) {
      notifyError('Delete failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const openAssign = async (wf) => {
    setSelectedWorkflow(wf);
    setAssignModalVisible(true);
    try {
      const res = await axios.get('/api/agents/for-assignment/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const agentList = res.data?.data || [];
      setAgents(agentList.map((a) => ({ label: a.name || 'Unnamed Agent', value: a.id })));
    } catch (err) {
      console.error('Agent fetch error:', err);
    }
  };

  const handleAssign = async () => {
    if (!selectedWorkflow || !selectedAgent) return;
    const wfId = effectiveWfId(selectedWorkflow);
    if (!wfId) {
      notifyError('Cannot assign: workflow has no valid ID');
      return;
    }
    try {
      await axios.post(
        `/api/agents/${selectedAgent.value}/assign-workflow/`,
        { workflow_id: wfId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setAssignModalVisible(false);
      setSelectedAgent(null);
      notifySuccess('Workflow assigned to agent');
      fetchWorkflows();
    } catch (err) {
      notifyError('Assign failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const openEdit = (wf) => {
    setSelectedWorkflow(wf);
    setEditForm({
      name: wf.name || '',
      description: wf.description || '',
      status: wf.status || 'active',
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    if (!selectedWorkflow) return;
    const wfId = effectiveWfId(selectedWorkflow);
    if (!wfId) {
      notifyError('Cannot update: workflow has no valid ID');
      return;
    }
    try {
      const payload = {
        name: editForm.name,
        description: editForm.description,
        status: editForm.status,
      };
      await axios.put(
        `/api/workflows/${wfId}/update/`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setWorkflows((prev) =>
        prev.map((w) =>
          effectiveWfId(w) === wfId
            ? { ...w, ...payload }
            : w
        )
      );
      setEditModalVisible(false);
      notifySuccess('Workflow updated');
    } catch (err) {
      notifyError('Update failed: ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: '/dashboard' },
            { text: 'Workflows', href: '/workflows' },
          ]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: 'Workflows',
        description: 'Workflows and tools powering your AI agents.',
        actions: (
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" onClick={fetchWorkflows}>
              Refresh
            </Button>
          </SpaceBetween>
        ),
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
      helpPanelHeader="Workflows"
      helpPanelContent={
        <div>
          <p>Workflows connect your agents to external tools, APIs, and automation logic.</p>
          <ul>
            <li>Browser automation</li>
            <li>Code execution</li>
            <li>Custom API integrations</li>
          </ul>
        </div>
      }
    >
      {loading ? (
        <Box textAlign="center" padding={{ top: 'xxxl' }}>
          <Spinner size="large" />
        </Box>
      ) : error ? (
        <Alert type="error" header="Error loading workflows">
          {error}
        </Alert>
      ) : (
        <SpaceBetween size="l">
          <ColumnLayout columns={{ default: 1, s: 2, m: 2, l: 4, xl: 4 }} variant="text-grid">
            <Container>
              <Box variant="awsui-key-label"><Icon name="script" variant="normal" /> Total Workflows</Box>
              <Box variant="h1">{workflows.length}</Box>
            </Container>
            <Container>
              <Box variant="awsui-key-label"><Icon name="user-profile" variant="normal" /> Assigned to Agents</Box>
              <Box variant="h1">{workflows.filter((w) => w.assigned_agent || w.assigned_agent_id || w.assigned_agent_name).length}</Box>
            </Container>
            <Container>
              <Box variant="awsui-key-label"><Icon name="settings" variant="normal" /> Reserved on Packs</Box>
              <Box variant="h1">{workflows.filter((w) => w.assigned_pack || w.assigned_pack_id || w.assigned_pack_name).length}</Box>
            </Container>
          </ColumnLayout>

          <Container
            header={
              <Header
                variant="h2"
                counter={`(${workflows.length})`}
                description="MCP tools and webhook automations connected to your agents"
                actions={
                  <Button iconName="add-plus" onClick={() => navigate('/workflow/new')}>
                    Add Workflow
                  </Button>
                }
              >
                Workflows
              </Header>
            }
          >
            <TextFilter
              filteringText={filteringText}
              filteringPlaceholder="Find workflows by name or type"
              onChange={({ detail }) => setFilteringText(detail.filteringText)}
            />
            <Table
              columnDefinitions={[
                {
                  id: 'name',
                  header: 'Name',
                  cell: (item) => item.name || 'Unnamed Workflow',
                  sortingField: 'name',
                },
                {
                  id: 'type',
                  header: 'Type',
                  cell: (item) => item.type || 'custom',
                  sortingField: 'type',
                },
                {
                  id: 'assigned_to',
                  header: 'Assigned to',
                  cell: (item) => {
                    if (item.assigned_agent_name || item.assigned_agent?.name) {
                      return <StatusIndicator type="success">{item.assigned_agent_name || item.assigned_agent?.name}</StatusIndicator>;
                    }
                    if (item.assigned_pack_name || item.assigned_pack?.name) {
                      return <StatusIndicator type="info">{item.assigned_pack_name || item.assigned_pack?.name} (pack)</StatusIndicator>;
                    }
                    return <StatusIndicator type="pending">Unassigned</StatusIndicator>;
                  },
                },
                {
                  id: 'status',
                  header: 'Status',
                  sortingField: 'status',
                  cell: (item) => {
                    if (item.sync_status === 'failed' || (!item.adexcloud_workflow_id && item.source === 'local')) {
                      return <StatusIndicator type="error">Sync failed</StatusIndicator>;
                    }
                    return (
                      <StatusIndicator type={item.status === 'active' || item.is_active !== false ? 'success' : 'stopped'}>
                        {item.status || (item.is_active !== false ? 'Active' : 'Inactive')}
                      </StatusIndicator>
                    );
                  },
                },
                {
                  id: 'actions',
                  header: 'Actions',
                  cell: (item) => (
                    <SpaceBetween direction="horizontal" size="xs">
                      {(item.sync_status === 'failed' || (!item.adexcloud_workflow_id && item.source === 'local')) && (
                        <Button
                          iconName="refresh"
                          variant="icon"
                          onClick={() => handleRetrySync(item)}
                          title="Retry sync to adexcloud"
                          loading={retryingId === item.id}
                        />
                      )}
                      <Button iconName="edit" variant="icon" onClick={() => openEdit(item)} title="Edit" />
                      <Button iconName="add-plus" variant="icon" onClick={() => openAssign(item)} title="Assign to agent" />
                      <Button iconName="remove" variant="icon" onClick={() => { setSelectedWorkflow(item); setDeleteModalVisible(true); }} title="Delete" />
                    </SpaceBetween>
                  ),
                },
              ]}
              items={sortedWorkflows}
              loadingText="Loading workflows"
              loading={loading}
              trackBy={(item) => item.id || item.adexcloud_workflow_id}
              stickyHeader
              sortable
              sortingColumn={sortingColumn}
              sortingDescending={sortingDescending}
              onSortingChange={({ detail }) => {
                setSortingColumn(detail.sortingColumn);
                setSortingDescending(detail.isDescending);
              }}
              empty={
                <Box textAlign="center" padding="l">
                  <SpaceBetween size="m">
                    <div>
                      <Box variant="h1" textAlign="center"><Icon name="script" size="big" variant="normal" /></Box>
                      <b>No workflows found</b>
                      <p>Create a workflow and assign it to an agent.</p>
                    </div>
                    <Button variant="primary" onClick={() => navigate('/workflow/new')}>Create Workflow</Button>
                  </SpaceBetween>
                </Box>
              }
            />
          </Container>
        </SpaceBetween>
      )}

      {/* Delete Modal */}
      {deleteModalVisible && (
        <Modal
          visible={deleteModalVisible}
          onDismiss={() => setDeleteModalVisible(false)}
          header="Delete Workflow"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeleteModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleDelete}>Delete</Button>
            </SpaceBetween>
          }
        >
          Are you sure you want to delete <b>{selectedWorkflow?.name}</b>?
        </Modal>
      )}

      {/* Assign Modal */}
      {assignModalVisible && (
        <Modal
          visible={assignModalVisible}
          onDismiss={() => setAssignModalVisible(false)}
          header="Assign Workflow to Agent"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setAssignModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleAssign}>Assign</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="l">
            <p>Select an agent to assign <b>{selectedWorkflow?.name}</b> to:</p>
            <Select
              placeholder="Choose agent"
              selectedOption={selectedAgent}
              onChange={({ detail }) => setSelectedAgent(detail.selectedOption)}
              options={agents}
            />
          </SpaceBetween>
        </Modal>
      )}

      {/* Edit Modal */}
      {editModalVisible && (
        <Modal
          visible={editModalVisible}
          onDismiss={() => setEditModalVisible(false)}
          header="Edit Workflow"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setEditModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleUpdate}>Save</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="l">
            <FormField label="Name" constraintText="Required">
              <Input
                value={editForm.name}
                onChange={({ detail }) => setEditForm((prev) => ({ ...prev, name: detail.value }))}
              />
            </FormField>
            <FormField label="Description">
              <Textarea
                value={editForm.description}
                onChange={({ detail }) => setEditForm((prev) => ({ ...prev, description: detail.value }))}
              />
            </FormField>
          </SpaceBetween>
        </Modal>
      )}
    </AppLayoutWrapper>
  );
}
